import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AllFeatureFlags } from '@models/mmobile';
import { spinner } from '@utils/rxjs';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import {
  DocumentError,
  DocumentErrorKey,
  DocumentTypes, HIDE_PARTICULAR_CONDITIONS_CIFS, PolicyDocumentPrefix,
  PolicyDocumentType
} from '../../models/document';
import {
  AutoOperation,
  HealthOperation,
  HomeOperation,
  LifeOperation,
  OthersOperation
} from '../../models/operation-register';
import { Document, Policy } from '../../models/policy';
import {
  isGeneralJudicialMutuality
} from '../../models/policy-health';
import { jsonStringify, padZeros } from '../../utils/utils';
import { LogService } from '../core/log.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { PoliciesHelperService } from '../core/policies-helper.service';
import { UiHelperService } from '../core/ui-helper.service';
import { PensionPoliciesService } from '../http-services/pension-policies.service';
import { FileHelperService } from './file-helper.service';

export enum ConditionsType {
  Particular,
  General
}

export type DownloadableDocumentsKey =
  | 'conditions'
  | 'renewalNotice'
  | 'liveBoundary'
  | 'liveCertificate'
  | 'individualCertificate'
  | 'civilLiability'
  | 'accidents';

@Injectable({
  providedIn: 'root'
})
export class PoliciesDocumentsHelperService {
  policy;

  constructor(
    private ui: UiHelperService,
    private log: LogService,
    private or: OperationRegisterService,
    private fileHelper: FileHelperService,
    private policiesHelper: PoliciesHelperService,
    private mmobileHelper: MMobileHelperService,
    private pensionPoliciesService: PensionPoliciesService,
  ) {}

  onDownloadDocument = async (policy, key: DownloadableDocumentsKey, value?: ConditionsType) => {
    this.policy = policy;
    let downloadObservable: Observable<any>;
    let policyDocumentType: PolicyDocumentType;

    switch (key) {
      case 'conditions':
        if (value === ConditionsType.Particular) {
          downloadObservable = this.policiesHelper.getParticularConditions(
            this.policy
          );
        } else if (value === ConditionsType.General) {
          policyDocumentType = PolicyDocumentType.GENERAL_CONDITIONS;
          downloadObservable = this.policiesHelper.getGeneralConditions(
            this.policy
          );
        } else {
          this.log.e(this, 'Unknown ConditionsType');
        }
        break;
      case 'renewalNotice':
        policyDocumentType = PolicyDocumentType.RENEWAL_NOTICE;
        downloadObservable = this.policiesHelper.getRenewalNotice(this.policy);
        break;
      case 'liveBoundary':
        policyDocumentType = PolicyDocumentType.LIVE_BOUNDARY;
        downloadObservable = this.policiesHelper.getPolicyReportDocument(
          this.policy,
          'BROCHURE'
        );
        break;
      case 'liveCertificate':
        policyDocumentType = PolicyDocumentType.LIVE_CERTIFICATE;
        downloadObservable = this.policiesHelper.getPolicyReportDocument(
          this.policy,
          'DUP'
        );
        break;
      case 'individualCertificate':
        policyDocumentType = PolicyDocumentType.INDIVIDUAL_CERTIFICATE;
        downloadObservable = this.policiesHelper.getPolicyReportDocument(
          this.policy,
          'CER'
        );
        break;
      case 'civilLiability':
        policyDocumentType = PolicyDocumentType.CIVIL_LIABILITY;
        downloadObservable = this.policiesHelper.getCivilLiabilityCertificate(
          this.policy);
        break;
      case 'accidents':
        policyDocumentType = PolicyDocumentType.ACCIDENT_CERTIFICATE;
        downloadObservable = this.policiesHelper.getAccidentCertificate(
          this.policy);
        break;
      default:
        this.log.e(this, 'Unknown download element type');
        return;
    }

    downloadObservable.pipe(spinner('policies-doc-helper')).subscribe(
      resFile => {
        // Register operation success
        this.registerOperation(key, value);
        if (resFile) {
          if (resFile.base64Content) {
            this.fileHelper
              .getDocumentFromBase64(
                resFile.base64Content,
                'application/pdf',
                `${this.generatePolicyFilePrefix(policyDocumentType)}${
                  policy.policyNumber
                }.pdf`
              )
              .then(document => this.fileHelper.open(document).catch(() => {}));
          } else if (resFile.type === 'application/pdf') {
            this.fileHelper
              .fileToPolicyDocument(resFile)
              .then((result: Document) => {
                result.name = 'AFParticipacionBeneficios';
                this.fileHelper.open(result).catch(() => {});
              });
          } else {
            this.handleDocumentError(null, policyDocumentType);
          }
        }
      },
      err => {
        this.handleDocumentError(err, policyDocumentType);
      }
    );
  };

  onGetReceipt = async (policy, paidReceipt) => {
    this.policy = policy;
    if (!this.policy || !paidReceipt) {
      this.ui.presentAlert(
        'CLIENT_AREA.CA_PRODUCT_DOCUMENTS.ERROR_NO_RECEIPT',
        'INFO'
      );
      return;
    }
    this.policiesHelper
      .getReceiptDocument(this.policy, paidReceipt)
      .pipe(spinner('policies-doc-helper'))
      .subscribe(
        (receiptFile: Document) => {
          if (receiptFile && receiptFile.base64Content) {
            this.fileHelper
              .getDocumentFromBase64(
                receiptFile.base64Content,
                'application/pdf',
                `Recibo_${paidReceipt.number}.pdf`
              )
              .then(document => this.fileHelper.open(document).catch(() => {}));
          } else {
            this.handleDocumentError();
          }
        },
        err => this.handleDocumentError(err)
      );
  };

  shouldShowRenewalNotice(policy: Policy , isBusiness = false) {
    let isRenewalNoticeEnabled: boolean;

    isRenewalNoticeEnabled =  (isBusiness)
    ? policy?.configuration?.operatives?.renewalNoticeEnabled
    : this.checkCardIsRenewalNoticeEnabled(policy);

    if (!isRenewalNoticeEnabled) {
      return false;
    }

    const renewalNoticeIntervalDays = +this.mmobileHelper.getCustomConfigData('renewalNoticeIntervalDays');
    const { expirationDate } = policy || ({} as any);
    if (!expirationDate) {
      return false;
    }

    const now = moment().startOf('day');
    const expirationMoment = moment(expirationDate);
    const renewalNoticeLimitDate = expirationMoment
      .clone()
      .subtract(renewalNoticeIntervalDays, 'days');

    const bottomLimitCondition = now.diff(renewalNoticeLimitDate) >= 0;
    const upperLimitCondition = now.diff(expirationMoment) <= 0;

    if (policy && policy.policyBaseType === 'health') {
      return (
        bottomLimitCondition &&
        upperLimitCondition &&
        this.policiesHelper.checkHealthRenewalNoticeAvailable(policy)
      );
    }

    return bottomLimitCondition && upperLimitCondition && !isGeneralJudicialMutuality(policy);
  }

  async onGetFiscalDuplicate(policy, exercise, isLifeCycle: boolean) {
    const codProduct = isLifeCycle ? policy.lifeCyclePolicyType : policy.productCode;

    const params = this.or.calculateDocumentROParams(policy, {
      type: DocumentTypes.DUPLICATE_FISCAL_CERTIFICATE,
      payload: { exercise, codProduct }
    });


    const download$ = isLifeCycle
      ? this.pensionPoliciesService.getPensionPolicyLifeCycleFiscalDuplicate(
          exercise,
          params,
          codProduct
        )
      : this.pensionPoliciesService.getPensionPolicyFiscalDuplicate(
          policy.policyNumber,
          policy.productCode,
          exercise,
          params
        );

    download$.pipe(spinner('client-area/fisca-duplicate')).subscribe(
      (response: Document) => {
        this.downloadFiscalDocument(response);
      },
      err => this.handleDocumentError(err)
    );
  }

  getTrimestralReportDocument(policy, exercise, period, isLifeCycle: boolean) {
    const codProduct = isLifeCycle ? policy.lifeCyclePolicyType : null;
    const params = this.or.calculateDocumentROParams(policy, {
      type: DocumentTypes.QUATERLY,
      payload: {
        exercise,
        period,
        codProduct
      }
    });
    if (isLifeCycle) {
      return this.pensionPoliciesService.getPensionPolicyLifeCycleQuarterlyReport(
        codProduct,
        exercise,
        period,
        params
      );
    } else {
      return this.pensionPoliciesService.getPensionPolicyQuarterlyReport(
        policy,
        policy.productCode,
        exercise,
        period,
        params
      );
    }
  }

  getPositionStatusDocument(policy, exercise, period, isLifeCycle: boolean) {
    const codProduct = isLifeCycle ? policy.lifeCyclePolicyType : null;
    const params = this.or.calculateDocumentROParams(policy, {
      type: DocumentTypes.QUATERLY,
      payload: {
        exercise,
        period,
        codProduct
      }
    });
    if (isLifeCycle) {
      return this.pensionPoliciesService.getPensionPolicyLifeCyclePositionStatusReport(
        codProduct,
        exercise,
        period,
        params
      );
    } else {
      return this.pensionPoliciesService.getPensionPolicyPositionStatusReport(
        policy,
        policy.productCode,
        exercise,
        period,
        params
      );
    }
  }

  canDownloadGeneralConditions(policy) {
    if (!policy) { return false; }
    type CCGGConfig = {
      online: boolean;
      companyId: string;
      department: string[];
    }[];
    const config: CCGGConfig = this.mmobileHelper.getCustomConfigData(
      'conditionalCCGG'
    );
    return (
      this.mmobileHelper.checkIsFeatureEnabled(
        AllFeatureFlags.GENERAL_CONDITIONS
      ) &&
      config.some(
        ({ online, companyId, department }) =>
          online &&
          companyId === policy.companyId &&
          this.isDepartamentIncluded(department, policy)
      )
    );
  }

  canDownloadParticularConditions(policy) {
    if (!policy) { return false; }
    type CCPPConfig = {
      companyId: string;
      department: string[];
    }[];
    const config: CCPPConfig = this.mmobileHelper.getCustomConfigData(
      'CCPP_DOWNLOAD_DISABLED'
    );
    return (
      !config?.some(
        ({ companyId, department }) =>
          companyId === policy.companyId &&
          department.includes(padZeros(policy.department, 3))
      ) &&
      (policy.policyBaseType !== 'health' ||
        !this.hideParticularConditions(policy?.holder?.documentNumber))
    );
  }

  hideParticularConditions(cif: string) {
    return HIDE_PARTICULAR_CONDITIONS_CIFS.includes(cif);
  }

  private isDepartamentIncluded(departamentList: string[], policy) {
    if (!departamentList || !departamentList?.length) {
      return true;
    }
    const departament = (policy?.policyBaseType === 'health') ? policy. productCode : policy.department;
    return departamentList.includes(padZeros(departament, 3));
  }

  handleDocumentError(error?: HttpErrorResponse, policyDocumentType?: PolicyDocumentType  ) {
    if (policyDocumentType === PolicyDocumentType.CIVIL_LIABILITY) {
      this.ui.presentOneButtonAlert({
        title: 'DOCUMENTS.ERROR.CIVIL_LIABILITY.NOT_FOUND_TITLE',
        description: 'DOCUMENTS.ERROR.CIVIL_LIABILITY.NOT_FOUND_DESCRIPTION',
        okText: 'DOCUMENTS.ERROR.CIVIL_LIABILITY.OK_TEXT'
      });
    } else {
      if (error?.status === 404) {
        this.ui.presentAlert(
          'DOCUMENTS.ERROR.NO_AVAILABLE_DOWNLOAD',
          null,
          null,
          'UNDERSTOOD'
        );
      } else {
        this.ui.presentOneButtonAlert({
          image: 'assets/images/result/error.svg',
          title: 'DOCUMENTS.ERROR.NOT_FOUND_TITLE',
          description: 'DOCUMENTS.ERROR.NOT_FOUND_DESCRIPTION'
        });
      }
    }
  }

  private downloadFiscalDocument(reportDocument: Document) {
    if (reportDocument && reportDocument.name && reportDocument.base64Content) {
      let receivedName = '';
      let receivedFormat = '';
      if (reportDocument.name.indexOf('.') !== -1) {
        receivedName = reportDocument.name.substring(
          0,
          reportDocument.name.lastIndexOf('.')
        );
        receivedFormat = reportDocument.name.substring(
          reportDocument.name.lastIndexOf('.'),
          reportDocument.name.length
        );
      } else {
        receivedName = reportDocument.name;
        receivedFormat = '.pdf';
      }
      let reportName = '';
      reportName += `${receivedName}_${this.getDateForFileName()}${receivedFormat}`;
      this.fileHelper
        .getDocumentFromBase64(
          reportDocument.base64Content,
          'application/pdf',
          reportName
        )
        .then(document => this.fileHelper.open(document).catch(() => {}));
    } else {
      this.ui.presentAlert(
        'CLIENT_AREA.CA_PRODUCT_DOCUMENTS.ERROR_NO_DOCUMENT',
        'INFO'
      );
    }
  }

  private getDateForFileName() {
    const date = new Date();
    const year = date.getFullYear();
    const month =
      date.getMonth() < 10 ? `0${date.getMonth()}` : `${date.getMonth()}`;
    const day =
      date.getDate() < 10 ? `0${date.getDate()}` : `${date.getDate()}`;
    const mins =
      date.getMinutes() < 10 ? `0${date.getMinutes()}` : `${date.getMinutes()}`;
    const seconds =
      date.getSeconds() < 10 ? `0${date.getSeconds()}` : `${date.getSeconds()}`;
    return `${year}${month}${day}${mins}${seconds}`;
  }

  private registerOperation = (
    key: string,
    value?: ConditionsType
  ) => {
    if (key === 'renewalNotice') {
        this.registerRenewalNoticeOk();
      }
    if (key === 'liveBoundary') {
        this.registerLiveBoundaryOk();
    }
  }

  private registerRenewalNoticeOk() {
    switch (this.policy.policyBaseType) {
      case 'home':
        this.or.registerHomeOperation(
          HomeOperation.AVISO_RENOVACION_OK,
          this.policy,
          ''
        );
        break;
      case 'car':
        this.or.registerAutoOperation(
          AutoOperation.AVISO_RENOVACION_OK,
          this.policy,
          ''
        );
        break;
      case 'saving':
      case 'life':
        this.or.registerLifeOperation(
          LifeOperation.AVISO_RENOVACION_OK,
          this.policy,
          ''
        );
        break;
      case 'health':
        this.or.registerHealthOperation(
          HealthOperation.AVISO_RENOVACION_OK,
          this.policy,
          ''
        );
        break;
      case 'death':
      case 'accident':
      case 'other':
        this.or.registerOthersOperation(
          OthersOperation.AVISO_RENOVACION_OK,
          this.policy,
          ''
        );
        break;
      default:
        return;
    }
  }

  private registerRenewalNoticeError(err) {
    const documentError = err as DocumentError;
    if (
      documentError.error.errorKey ===
      DocumentErrorKey.DOCUMENTAL_MANAGEMENT_NOT_FOUND
    ) {
      // Register not found error
      return this.registerRenewalNoticeUnavailable(err);
    }
    // Register generic error
    const structure = jsonStringify(err.error || err);
    switch (this.policy.policyBaseType) {
      case 'home':
        this.or.registerHomeOperation(
          HomeOperation.AVISO_RENOVACION_ERROR,
          this.policy,
          structure
        );
        break;
      case 'car':
        this.or.registerAutoOperation(
          AutoOperation.AVISO_RENOVACION_ERROR,
          this.policy,
          structure
        );
        break;
      case 'saving':
      case 'life':
        this.or.registerLifeOperation(
          LifeOperation.AVISO_RENOVACION_ERROR,
          this.policy,
          structure
        );
        break;
      case 'health':
        this.or.registerHealthOperation(
          HealthOperation.AVISO_RENOVACION_ERROR,
          this.policy,
          structure
        );
        break;
      case 'death':
      case 'accident':
      case 'other':
        this.or.registerOthersOperation(
          OthersOperation.AVISO_RENOVACION_ERROR,
          this.policy,
          structure
        );
        break;
      default:
        return;
    }
  }

  private registerLiveBoundaryOk() {
    if (this.policy.policyBaseType === 'saving') {
      this.or.registerLifeOperation(
        LifeOperation.PARTICIPACION_BENEFICIOS_OK,
        this.policy,
        ''
      );
    }
  }

  /*
   * TODO: This is unused right now because it depends on the presence
   *       of a renewal notice on Mailteck
   */
  private registerRenewalNoticeUnavailable(err) {
    let structure = '';
    if (err && err.requestId) {
      structure = `requestId: ${err.requestId}`;
    }
    switch (this.policy.policyBaseType) {
      case 'home':
        this.or.registerHomeOperation(
          HomeOperation.AVISO_RENOVACION_NO_DISPONIBLE,
          this.policy,
          structure
        );
        break;
      case 'car':
        this.or.registerAutoOperation(
          AutoOperation.AVISO_RENOVACION_NO_DISPONIBLE,
          this.policy,
          structure
        );
        break;
      case 'saving':
      case 'life':
        this.or.registerLifeOperation(
          LifeOperation.AVISO_RENOVACION_NO_DISPONIBLE,
          this.policy,
          structure
        );
        break;
      case 'health':
        this.or.registerHealthOperation(
          HealthOperation.AVISO_RENOVACION_NO_DISPONIBLE,
          this.policy,
          structure
        );
        break;
      case 'death':
      case 'accident':
      case 'other':
        this.or.registerOthersOperation(
          OthersOperation.AVISO_RENOVACION_NO_DISPONIBLE,
          this.policy,
          structure
        );
        break;
      default:
        return;
    }
  }

  private generatePolicyFilePrefix(policyDocumentType: PolicyDocumentType) {
    let prefix = '';
    switch (policyDocumentType) {
      case PolicyDocumentType.POLICY:
        prefix = PolicyDocumentPrefix.POLICY as string;
        switch (this.policy.policyBaseType) {
          case 'car':
            prefix += 'Auto_';
            break;
          case 'home':
            prefix += 'Hogar_';
            break;
          default:
            break;
        }
        break;
      case PolicyDocumentType.GENERAL_CONDITIONS:
        prefix = PolicyDocumentPrefix.GENERAL_CONDITIONS as string;
        break;
      case PolicyDocumentType.RENEWAL_NOTICE:
        prefix = PolicyDocumentPrefix.RENEWAL_NOTICE as string;
        break;
      case PolicyDocumentType.LIVE_CERTIFICATE:
        prefix = PolicyDocumentPrefix.LIVE_CERTIFICATE as string;
        break;
      case PolicyDocumentType.ACCIDENT_CERTIFICATE:
        prefix = PolicyDocumentPrefix.ACCIDENT_CERTIFICATE as string;
        break;
      default:
        break;
    }
    return prefix;
  }

  private checkCardIsRenewalNoticeEnabled(policy: Policy) {
    const mmobileCards = this.mmobileHelper.getCustomConfigData('tarjetas');
    switch (policy?.policyBaseType) {
      case 'home': {
        return mmobileCards?.tarjetaHogar?.renewalNotice;
      }
      case 'car': {
        return mmobileCards?.tarjetaAutos?.renewalNotice;
      }
      case 'life': {
        return mmobileCards?.tarjetaVidaRiesgo?.renewalNotice;
      }
      case 'saving': {
        return mmobileCards?.tarjetaVidaAhorro?.renewalNotice;
      }
      case 'health': {
        return mmobileCards?.tarjetaSalud?.renewalNotice;
      }
      case 'other': {
        return mmobileCards?.tarjetaOtros?.renewalNotice;
      }
      case 'accident': {
        return mmobileCards?.tarjetaAccidentes?.renewalNotice;
      }
      case 'death': {
        return mmobileCards?.tarjetaDecesos?.renewalNotice;
      }
      default:
        break;
    }
    return false;
  }
}
